import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  GitCompare,
  Thermometer,
  Droplets,
  Sun,
  Ruler,
  MapPin,
  HeartPulse,
  Utensils,
  HandMetal,
  X,
  Check,
  ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuideComparePage = () => {
  const [params, setParams] = useSearchParams();
  const [guides, setGuides] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [leftSlug, setLeftSlug] = useState(params.get("a") || "");
  const [rightSlug, setRightSlug] = useState(params.get("b") || "");
  const [left, setLeft] = useState(null);
  const [right, setRight] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/guides`);
        setGuides((data.guides || []).sort((a, b) => a.common_name.localeCompare(b.common_name)));
      } catch (e) {
        console.error("Failed to load guides list:", e);
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  // Fetch detail when slug changes + keep URL in sync
  useEffect(() => {
    const next = new URLSearchParams();
    if (leftSlug) next.set("a", leftSlug);
    if (rightSlug) next.set("b", rightSlug);
    setParams(next, { replace: true });
  }, [leftSlug, rightSlug, setParams]);

  useEffect(() => {
    if (!leftSlug) { setLeft(null); return; }
    axios.get(`${API}/guides/${leftSlug}`).then((r) => setLeft(r.data)).catch(() => setLeft(null));
  }, [leftSlug]);

  useEffect(() => {
    if (!rightSlug) { setRight(null); return; }
    axios.get(`${API}/guides/${rightSlug}`).then((r) => setRight(r.data)).catch(() => setRight(null));
  }, [rightSlug]);

  const rows = useMemo(() => ([
    { key: "difficulty", label: "Difficulty", icon: null },
    { key: "origin", label: "Origin", icon: MapPin },
    { key: "adult_size_cm", label: "Adult size", icon: Ruler },
    { key: "lifespan_years", label: "Lifespan", icon: null },
    { key: "enclosure_min_ft", label: "Min. enclosure", icon: null },
    { key: "temp_basking_surface_c", label: "Basking °C", icon: Thermometer, suffix: " °C" },
    { key: "temp_warm_hide_c", label: "Warm hide °C", icon: Thermometer, suffix: " °C" },
    { key: "temp_ambient_cool_c", label: "Ambient °C", icon: Thermometer, suffix: " °C" },
    { key: "temp_night_min_c", label: "Night min °C", icon: Thermometer, suffix: " °C" },
    { key: "humidity_percent", label: "Humidity", icon: Droplets },
    { key: "uvi_target", label: "UVI target", icon: Sun },
    { key: "uvb_bulb", label: "UVB bulb", icon: Sun },
    { key: "photoperiod_summer", label: "Photoperiod (summer)", icon: null },
    { key: "substrate", label: "Substrate", icon: null },
    { key: "diet", label: "Diet", icon: Utensils },
    { key: "handling", label: "Handling", icon: HandMetal },
    { key: "common_health_issues", label: "Common health issues", icon: HeartPulse },
  ]), []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F1DE] pt-24 pb-16" data-testid="guide-compare-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/guides"
          className="flex items-center gap-1.5 text-xs text-[#F4F1DE]/60 hover:text-[#E07A5F] mb-6 uppercase tracking-widest w-fit"
          data-testid="back-to-guides-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All care guides
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-2 text-[#E07A5F] text-xs uppercase tracking-widest mb-3">
            <GitCompare className="w-4 h-4" />
            <span>Side-by-side comparison</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
            Compare <span className="text-[#E07A5F]">two species</span>
          </h1>
          <p className="text-sm text-[#F4F1DE]/60 max-w-2xl">
            Pick two reptiles you're deciding between. We'll line up the husbandry requirements
            side-by-side so you can see exactly where the commitment differs.
          </p>
        </div>

        {/* Species pickers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <SpeciesPicker
            label="Species A"
            options={guides}
            value={leftSlug}
            onChange={setLeftSlug}
            loading={loadingList}
            testid="picker-a"
          />
          <SpeciesPicker
            label="Species B"
            options={guides}
            value={rightSlug}
            onChange={setRightSlug}
            loading={loadingList}
            testid="picker-b"
          />
        </div>

        {/* Comparison grid */}
        {!left && !right ? (
          <div className="border border-dashed border-white/10 p-10 text-center text-[#F4F1DE]/40" data-testid="compare-empty">
            <GitCompare className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Pick two species above to see a full husbandry comparison.</p>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#121212] overflow-hidden" data-testid="compare-table">
            {/* Headers */}
            <div className="grid grid-cols-[minmax(140px,_1fr)_1.3fr_1.3fr] border-b border-white/10">
              <div className="p-4 text-[10px] uppercase tracking-widest text-[#F4F1DE]/40 bg-[#0A0A0A]">
                Parameter
              </div>
              <SpeciesHeader guide={left} />
              <SpeciesHeader guide={right} />
            </div>

            {/* Rows */}
            {rows.map((row, i) => (
              <ComparisonRow
                key={row.key}
                row={row}
                left={left}
                right={right}
                zebra={i % 2 === 1}
              />
            ))}

            {/* CTA row */}
            {(left || right) && (
              <div className="grid grid-cols-[minmax(140px,_1fr)_1.3fr_1.3fr] border-t-2 border-[#E07A5F]/30">
                <div className="p-4 text-xs text-[#F4F1DE]/50 bg-[#0A0A0A]">Build CTA</div>
                <div className="p-4 border-l border-white/5">
                  {left && (
                    <Link
                      to={`/guides/${left.slug}`}
                      className="inline-flex items-center gap-1 text-xs text-[#E07A5F] hover:underline uppercase tracking-widest"
                      data-testid={`cta-read-${left.slug}`}
                    >
                      Full guide →
                    </Link>
                  )}
                </div>
                <div className="p-4 border-l border-white/5">
                  {right && (
                    <Link
                      to={`/guides/${right.slug}`}
                      className="inline-flex items-center gap-1 text-xs text-[#E07A5F] hover:underline uppercase tracking-widest"
                      data-testid={`cta-read-${right.slug}`}
                    >
                      Full guide →
                    </Link>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Beginner-friendly comparison */}
        {left && right && (
          <BeginnerSummary left={left} right={right} />
        )}
      </div>
    </div>
  );
};

const SpeciesPicker = ({ label, options, value, onChange, loading, testid }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.slug === value);
  return (
    <div className="relative" data-testid={testid}>
      <label className="block text-[10px] uppercase tracking-widest text-[#F4F1DE]/40 mb-2 font-semibold">
        {label}
      </label>
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className="w-full flex items-center justify-between gap-3 bg-[#121212] border border-white/10 px-4 py-3 text-sm text-[#F4F1DE] hover:border-[#E07A5F]/50 transition-colors"
        data-testid={`${testid}-button`}
      >
        {selected ? (
          <div className="flex items-center gap-3 min-w-0">
            {selected.image_url && (
              <img src={selected.image_url} alt="" className="w-8 h-8 object-cover border border-white/10" />
            )}
            <div className="text-left min-w-0">
              <p className="truncate font-semibold">{selected.common_name}</p>
              <p className="text-[10px] italic text-[#F4F1DE]/40 truncate">{selected.latin_name}</p>
            </div>
          </div>
        ) : (
          <span className="text-[#F4F1DE]/40">Choose a species…</span>
        )}
        <ChevronDown className={`w-4 h-4 text-[#F4F1DE]/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute z-20 mt-1 w-full max-h-80 overflow-auto bg-[#121212] border border-white/10 shadow-2xl"
          data-testid={`${testid}-dropdown`}
        >
          {options.map((o) => (
            <button
              key={o.slug}
              onClick={() => { onChange(o.slug); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-[#E07A5F]/10 ${
                o.slug === value ? "bg-[#E07A5F]/20 text-[#E07A5F]" : "text-[#F4F1DE]"
              }`}
              data-testid={`${testid}-option-${o.slug}`}
            >
              {o.image_url && (
                <img src={o.image_url} alt="" className="w-6 h-6 object-cover border border-white/10" />
              )}
              <span className="flex-1 truncate">{o.common_name}</span>
              <span className="text-[10px] text-[#F4F1DE]/40">{o.difficulty?.split(" ")[0]}</span>
              {o.slug === value && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}

      {value && (
        <button
          onClick={() => onChange("")}
          className="absolute top-8 right-10 w-6 h-6 flex items-center justify-center text-[#F4F1DE]/40 hover:text-[#E07A5F]"
          data-testid={`${testid}-clear`}
          title="Clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

const SpeciesHeader = ({ guide }) => (
  <div className="p-4 border-l border-white/5">
    {guide ? (
      <div className="flex items-center gap-3">
        {guide.image_url && (
          <img
            src={guide.image_url}
            alt=""
            className="w-12 h-12 object-cover border border-white/10 shrink-0"
          />
        )}
        <div className="min-w-0">
          <p className="font-bold text-[#F4F1DE] truncate">{guide.common_name}</p>
          <p className="text-[10px] italic text-[#F4F1DE]/40 truncate">{guide.latin_name}</p>
        </div>
      </div>
    ) : (
      <p className="text-[#F4F1DE]/30 text-sm italic">— pick a species —</p>
    )}
  </div>
);

const ComparisonRow = ({ row, left, right, zebra }) => {
  const Icon = row.icon;
  return (
    <div className={`grid grid-cols-[minmax(140px,_1fr)_1.3fr_1.3fr] border-b border-white/5 ${zebra ? "bg-[#0c0c0c]" : ""}`}>
      <div className="p-3 text-[11px] uppercase tracking-widest text-[#F4F1DE]/50 bg-[#0A0A0A] flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#E07A5F]" />}
        {row.label}
      </div>
      <div className="p-3 text-sm text-[#F4F1DE] border-l border-white/5 leading-relaxed">
        {left ? fmt(left[row.key], row.suffix) : <span className="text-[#F4F1DE]/20">—</span>}
      </div>
      <div className="p-3 text-sm text-[#F4F1DE] border-l border-white/5 leading-relaxed">
        {right ? fmt(right[row.key], row.suffix) : <span className="text-[#F4F1DE]/20">—</span>}
      </div>
    </div>
  );
};

const fmt = (v, suffix) => {
  if (v === undefined || v === null || v === "") return <span className="text-[#F4F1DE]/30">—</span>;
  const text = typeof v === "string" ? v : String(v);
  return suffix ? `${text}${suffix}` : text;
};

const BeginnerSummary = ({ left, right }) => {
  // Surface the 2-3 biggest commitment differences for a quick takeaway
  const points = [];
  if (left.difficulty !== right.difficulty) {
    points.push(`${left.common_name} is rated ${left.difficulty.split(" ")[0]}, ${right.common_name} is ${right.difficulty.split(" ")[0]}.`);
  }
  if (left.heat_source !== right.heat_source) {
    const map = { bulb: "needs a basking lamp", mat: "uses an under-tank heat mat", none: "needs no extra heating" };
    points.push(`${left.common_name} ${map[left.heat_source] || "has different heat needs"}; ${right.common_name} ${map[right.heat_source] || "differs"}.`);
  }
  if (left.enclosure_min_ft && right.enclosure_min_ft && left.enclosure_min_ft !== right.enclosure_min_ft) {
    points.push(`Min. enclosure: ${left.common_name} ${left.enclosure_min_ft} vs ${right.common_name} ${right.enclosure_min_ft}.`);
  }
  if (left.lifespan_years && right.lifespan_years && left.lifespan_years !== right.lifespan_years) {
    points.push(`Lifespan: ${left.common_name} ${left.lifespan_years}; ${right.common_name} ${right.lifespan_years}.`);
  }
  if (!points.length) return null;
  return (
    <div className="mt-8 p-5 border border-[#E07A5F]/30 bg-gradient-to-br from-[#E07A5F]/5 to-transparent" data-testid="beginner-summary">
      <p className="text-xs uppercase tracking-widest text-[#E07A5F] mb-3 font-semibold">Quick take</p>
      <ul className="space-y-2 text-sm text-[#F4F1DE]/80">
        {points.slice(0, 4).map((p, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-[#E07A5F] shrink-0">—</span>
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default GuideComparePage;
